import * as React from 'react';
import { Check, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Toast component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 177:2137):
 * - Border Radius: 6px
 * - Padding: 8px 12px
 * - Variants: Success, Warning, Error, Info
 * - Styles: Fill (colored bg), Outline (white bg with border)
 */
const toastVariants = cva(
  'flex items-center gap-2 px-3 py-2 rounded-md text-sm',
  {
    variants: {
      variant: {
        success: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200',
        warning: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200',
        error: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
        info: 'bg-grey-100 text-grey-800 dark:bg-grey-800 dark:text-grey-200',
      },
      outline: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      { variant: 'success', outline: true, className: 'bg-transparent border border-green-400 dark:border-green-600' },
      { variant: 'warning', outline: true, className: 'bg-transparent border border-orange-200 dark:border-orange-600' },
      { variant: 'error', outline: true, className: 'bg-transparent border border-red-300 dark:border-red-600' },
      { variant: 'info', outline: true, className: 'bg-transparent border border-grey-300 dark:border-grey-600' },
    ],
    defaultVariants: {
      variant: 'success',
      outline: false,
    },
  }
);

const toastIcons = {
  success: Check,
  warning: AlertTriangle,
  error: AlertCircle,
  info: Info,
};

const Toast = React.forwardRef(
  ({ className, variant = 'success', outline = false, children, action, onClose, ...props }, ref) => {
    const Icon = toastIcons[variant];
    return (
      <div
        ref={ref}
        className={cn(toastVariants({ variant, outline }), className)}
        role="alert"
        {...props}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1">{children}</span>
        {action && (
          <button className="font-medium hover:underline" onClick={action.onClick}>
            {action.label}
          </button>
        )}
        {onClose && (
          <button
            onClick={onClose}
            className="ml-1 p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/10"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }
);
Toast.displayName = 'Toast';

export { Toast, toastVariants };
